import { supabase } from './supabase';
import { normalizeCommentBody, shapeComment } from './socialUtils';

// Central data-access layer for Orbit, backed by the self-hosted
// Supabase instance. Mirrors the schema/RLS already deployed for the
// Next.js Orbit build (orbit_profiles / orbit_posts / orbit_reactions /
// orbit_poll_options / orbit_poll_votes / orbit_galaxies /
// orbit_galaxy_members / orbit_friends) -- this frontend is a second
// client against the exact same backend, not a separate app.

export async function getCurrentUser() {
  const {
    data: { user },
  } = await supabase.auth.getUser();
  return user;
}

export async function getMyProfile() {
  const user = await getCurrentUser();
  if (!user) return null;
  const { data, error } = await supabase
    .from('orbit_profiles')
    .select('*')
    .eq('id', user.id)
    .maybeSingle();
  if (error) throw error;
  return data;
}

export async function updateMyProfile(patch) {
  const user = await getCurrentUser();
  if (!user) throw new Error('Not authenticated');
  // Upsert, not update: a brand-new user has no orbit_profiles row yet
  // (nothing creates one automatically on signInWithOtp), so this is
  // often the very first write for that id.
  const { data, error } = await supabase
    .from('orbit_profiles')
    .upsert({ id: user.id, ...patch })
    .select()
    .single();
  if (error) throw error;
  return data;
}

// --- Posts -----------------------------------------------------------

const POST_SELECT = `
  id, body, image_url, feeling, location, audience, audience_galaxy_id,
  music_track, music_artist, music_art_url, created_at, author_id,
  author:orbit_profiles!orbit_posts_author_id_fkey ( id, username, display_name, avatar_url ),
  poll_options:orbit_poll_options ( id, label, position ),
  reactions:orbit_reactions ( user_id ),
  comments:orbit_comments ( count )
`;

function shapePost(row, myUserId) {
  const reactionCount = row.reactions?.length ?? 0;
  const reactedByMe = !!row.reactions?.some((r) => r.user_id === myUserId);
  return {
    id: row.id,
    authorHandle: row.author?.username ?? 'unknown',
    authorName: row.author?.display_name ?? row.author?.username ?? 'Unknown',
    avatar: row.author?.avatar_url || null,
    time: row.created_at,
    text: row.body,
    image: row.image_url,
    galaxy: row.audience === 'galaxy' ? row.audience_galaxy_id : null,
    audience: row.audience,
    pinned: false,
    feel: row.feeling,
    location: row.location,
    song: row.music_track ? { title: row.music_track, artist: row.music_artist, cover: row.music_art_url } : null,
    poll: row.poll_options?.length
      ? { options: row.poll_options.sort((a, b) => a.position - b.position) }
      : null,
    counts: { stars: reactionCount, echoes: row.comments?.[0]?.count ?? 0, relays: 0, rockets: 0 },
    reactedByMe,
    authorId: row.author_id,
  };
}

export async function fetchCosmosPosts(myUserId) {
  const { data, error } = await supabase
    .from('orbit_posts')
    .select(POST_SELECT)
    .eq('audience', 'cosmos')
    .order('created_at', { ascending: false })
    .limit(50);
  if (error) throw error;
  return (data ?? []).map((r) => shapePost(r, myUserId));
}

export async function fetchFriendsPosts(myUserId) {
  const { data, error } = await supabase
    .from('orbit_posts')
    .select(POST_SELECT)
    .eq('audience', 'friends')
    .order('created_at', { ascending: false })
    .limit(50);
  if (error) throw error;
  return (data ?? []).map((r) => shapePost(r, myUserId));
}

export async function fetchGalaxyPosts(galaxyIds, myUserId) {
  if (!galaxyIds?.length) return [];
  const { data, error } = await supabase
    .from('orbit_posts')
    .select(POST_SELECT)
    .eq('audience', 'galaxy')
    .in('audience_galaxy_id', galaxyIds)
    .order('created_at', { ascending: false })
    .limit(50);
  if (error) throw error;
  return (data ?? []).map((r) => shapePost(r, myUserId));
}

export async function fetchMyPosts(myUserId) {
  const { data, error } = await supabase
    .from('orbit_posts')
    .select(POST_SELECT)
    .eq('author_id', myUserId)
    .order('created_at', { ascending: false })
    .limit(100);
  if (error) throw error;
  return (data ?? []).map((r) => shapePost(r, myUserId));
}

export async function fetchPostById(postId, myUserId) {
  const { data, error } = await supabase
    .from('orbit_posts')
    .select(POST_SELECT)
    .eq('id', postId)
    .maybeSingle();
  if (error) throw error;
  return data ? shapePost(data, myUserId) : null;
}

const COMMENT_SELECT = `
  id, post_id, body, created_at,
  author:orbit_profiles!orbit_comments_author_id_fkey ( id, username, display_name, avatar_url )
`;

export async function fetchComments(postId) {
  const { data, error } = await supabase
    .from('orbit_comments')
    .select(COMMENT_SELECT)
    .eq('post_id', postId)
    .order('created_at', { ascending: true });
  if (error) throw error;
  return (data ?? []).map(shapeComment);
}

export async function createComment(postId, body) {
  const user = await getCurrentUser();
  if (!user) throw new Error('Not authenticated');

  const { data, error } = await supabase
    .from('orbit_comments')
    .insert({ post_id: postId, author_id: user.id, body: normalizeCommentBody(body) })
    .select(COMMENT_SELECT)
    .single();
  if (error) throw error;
  return shapeComment(data);
}

export async function deleteComment(commentId) {
  const { error } = await supabase.from('orbit_comments').delete().eq('id', commentId);
  if (error) throw error;
}

export async function createPost({ text, image, galaxy, privacy, feel, location, poll, song }) {
  const user = await getCurrentUser();
  if (!user) throw new Error('Not authenticated');

  const audience = privacy === 'Public Galaxy' && galaxy ? 'galaxy' : privacy === 'Inner Orbit Only' ? 'friends' : 'cosmos';

  const insertRow = {
    author_id: user.id,
    body: text || '',
    image_url: image || null,
    feeling: feel || null,
    location: location || null,
    audience,
    audience_galaxy_id: audience === 'galaxy' ? galaxy : null,
    music_track: song?.title || null,
    music_artist: song?.artist || null,
    music_art_url: song?.cover || null,
  };

  const { data: post, error } = await supabase.from('orbit_posts').insert(insertRow).select().single();
  if (error) throw error;

  if (poll?.question && poll.options?.length) {
    const rows = poll.options.map((label, position) => ({ post_id: post.id, label, position }));
    const { error: pollError } = await supabase.from('orbit_poll_options').insert(rows);
    if (pollError) throw pollError;
  }

  return post;
}

export async function toggleReaction(postId, currentlyReacted) {
  const user = await getCurrentUser();
  if (!user) throw new Error('Not authenticated');

  if (currentlyReacted) {
    const { error } = await supabase.from('orbit_reactions').delete().eq('post_id', postId).eq('user_id', user.id);
    if (error) throw error;
  } else {
    const { data: post, error: postError } = await supabase
      .from('orbit_posts')
      .select('author_id')
      .eq('id', postId)
      .single();
    if (postError) throw postError;

    const { error } = await supabase.from('orbit_reactions').insert({ post_id: postId, user_id: user.id });
    if (error) throw error;

    if (post.author_id !== user.id) {
      const { error: notificationError } = await supabase.from('orbit_notifications').insert({
        user_id: post.author_id,
        actor_id: user.id,
        type: 'reaction',
        post_id: postId,
      });
      if (notificationError) throw notificationError;
    }
  }
}

// --- Notifications -----------------------------------------------------

export async function fetchNotifications(myUserId) {
  const { data, error } = await supabase
    .from('orbit_notifications')
    .select(`
      id, type, post_id, galaxy_id, read, created_at,
      actor:orbit_profiles!orbit_notifications_actor_id_fkey ( id, username, display_name, avatar_url )
    `)
    .eq('user_id', myUserId)
    .order('created_at', { ascending: false })
    .limit(50);
  if (error) throw error;
  return (data ?? []).map((row) => ({
    id: row.id,
    type: row.type,
    postId: row.post_id,
    galaxyId: row.galaxy_id,
    read: row.read,
    createdAt: row.created_at,
    actor: row.actor ? {
      id: row.actor.id,
      handle: row.actor.username,
      name: row.actor.display_name,
      avatar: row.actor.avatar_url,
    } : null,
  }));
}

export async function markNotificationRead(id) {
  const { error } = await supabase.from('orbit_notifications').update({ read: true }).eq('id', id);
  if (error) throw error;
}

// --- Galaxies ----------------------------------------------------------

export async function fetchMyGalaxies(myUserId) {
  const { data: memberships, error: memErr } = await supabase
    .from('orbit_galaxy_members')
    .select('galaxy_id')
    .eq('user_id', myUserId);
  if (memErr) throw memErr;

  const ids = (memberships ?? []).map((m) => m.galaxy_id);
  if (!ids.length) return [];

  const { data, error } = await supabase.from('orbit_galaxies').select('*').in('id', ids);
  if (error) throw error;
  return data ?? [];
}

export async function createGalaxy(name) {
  const user = await getCurrentUser();
  if (!user) throw new Error('Not authenticated');

  const { data: galaxy, error } = await supabase
    .from('orbit_galaxies')
    .insert({ name, owner_id: user.id, is_public: true })
    .select()
    .single();
  if (error) throw error;

  const { error: memberError } = await supabase
    .from('orbit_galaxy_members')
    .insert({ galaxy_id: galaxy.id, user_id: user.id });
  if (memberError) throw memberError;

  return galaxy;
}

export async function fetchPublicGalaxies({ excludeMine = true } = {}) {
  const user = await getCurrentUser();
  if (!user) throw new Error('Not authenticated');

  const { data: memberships, error: membershipError } = await supabase
    .from('orbit_galaxy_members')
    .select('galaxy_id')
    .eq('user_id', user.id);
  if (membershipError) throw membershipError;

  const myGalaxyIds = (memberships ?? []).map((membership) => membership.galaxy_id);
  let query = supabase
    .from('orbit_galaxies')
    .select('id, name, owner_id, created_at, is_public, orbit_galaxy_members(count)')
    .eq('is_public', true)
    .order('created_at', { ascending: false })
    .limit(50);

  if (excludeMine && myGalaxyIds.length) {
    query = query.not('id', 'in', `(${myGalaxyIds.join(',')})`);
  }

  const { data, error } = await query;
  if (error) throw error;
  const mine = new Set(myGalaxyIds);
  return (data ?? []).map((row) => ({
    id: row.id,
    name: row.name,
    ownerId: row.owner_id,
    createdAt: row.created_at,
    isPublic: row.is_public,
    memberCount: row.orbit_galaxy_members?.[0]?.count ?? 0,
    joined: mine.has(row.id),
  }));
}

export async function joinGalaxy(galaxyId) {
  const user = await getCurrentUser();
  if (!user) throw new Error('Not authenticated');
  const { data, error } = await supabase
    .from('orbit_galaxy_members')
    .insert({ galaxy_id: galaxyId, user_id: user.id })
    .select()
    .single();
  if (error) throw error;
  return data;
}

// --- Friends -------------------------------------------------------------

export async function fetchFriends(myUserId) {
  const { data, error } = await supabase
    .from('orbit_friends')
    .select('friend_id, profile:orbit_profiles!orbit_friends_friend_id_fkey ( id, username, display_name, avatar_url )')
    .eq('user_id', myUserId);
  if (error) throw error;
  return (data ?? []).map((r) => ({
    id: r.friend_id,
    handle: r.profile?.username,
    name: r.profile?.display_name,
    avatar: r.profile?.avatar_url,
    orbiting: true,
  }));
}

export async function addFriend(friendId) {
  const user = await getCurrentUser();
  if (!user) throw new Error('Not authenticated');
  const { error } = await supabase.from('orbit_friends').insert({ user_id: user.id, friend_id: friendId });
  if (error) throw error;

  const { error: notificationError } = await supabase.from('orbit_notifications').insert({
    user_id: friendId,
    actor_id: user.id,
    type: 'friend_add',
  });
  if (notificationError) throw notificationError;
}

export async function removeFriend(friendId) {
  const user = await getCurrentUser();
  if (!user) throw new Error('Not authenticated');
  const { error } = await supabase.from('orbit_friends').delete().eq('user_id', user.id).eq('friend_id', friendId);
  if (error) throw error;
}

// --- Search / discovery ---------------------------------------------------

// orbit_profiles is publicly readable (RLS: "orbit_profiles_public_read"),
// so this works for any signed-in user searching for anyone, friend or
// not -- matches on username or display_name, case-insensitive, excludes
// yourself and (optionally) people you're already friends with.
export async function searchProfiles(query, { excludeIds = [] } = {}) {
  const trimmed = (query || '').trim();
  if (!trimmed) return [];

  const user = await getCurrentUser();
  const exclude = new Set([user?.id, ...excludeIds].filter(Boolean));

  const { data, error } = await supabase
    .from('orbit_profiles')
    .select('id, username, display_name, avatar_url')
    .or(`username.ilike.%${trimmed}%,display_name.ilike.%${trimmed}%`)
    .limit(20);
  if (error) throw error;

  return (data ?? []).filter((p) => !exclude.has(p.id));
}

// --- Galaxy detail ---------------------------------------------------------

export async function fetchGalaxyById(galaxyId) {
  const { data, error } = await supabase.from('orbit_galaxies').select('*').eq('id', galaxyId).maybeSingle();
  if (error) throw error;
  return data;
}

export async function fetchGalaxyMembers(galaxyId) {
  const { data, error } = await supabase
    .from('orbit_galaxy_members')
    .select('user_id, profile:orbit_profiles!orbit_galaxy_members_user_id_fkey ( id, username, display_name, avatar_url )')
    .eq('galaxy_id', galaxyId);
  if (error) throw error;
  return (data ?? []).map((r) => ({
    id: r.user_id,
    handle: r.profile?.username,
    name: r.profile?.display_name,
    avatar: r.profile?.avatar_url,
  }));
}

// --- Uploads (via the FastAPI backend -> Cloudflare R2) -----------------

const API_BASE = process.env.REACT_APP_API_BASE || '';

export async function uploadImage(file) {
  const {
    data: { session },
  } = await supabase.auth.getSession();
  if (!session) throw new Error('Not authenticated');

  const form = new FormData();
  form.append('file', file);

  const res = await fetch(`${API_BASE}/api/orbit/upload`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${session.access_token}` },
    body: form,
  });

  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.detail || 'Upload failed');
  }

  const data = await res.json();
  return data.url;
}

// --- Feedback / bug reports ------------------------------------------------
// Unauthenticated on purpose -- reachable from the top bar whether or not
// the visitor is signed in. Backend forwards it to bot@de1ta.space by
// SMTP; validation happens client-side first (validateFeedbackDraft) so
// the request only goes out once it's already well-formed.
export async function submitFeedback({ message, contactEmail, page }) {
  const res = await fetch(`${API_BASE}/api/feedback`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ message, contactEmail: contactEmail || undefined, page }),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.detail || 'Could not send feedback');
  }
  return res.json();
}
